// src/pages/school/CreateQuestionBank.js
import React, { useEffect, useMemo, useRef, useState, useCallback, useMemo as useMemo2 } from "react";
import {
  Card, Row, Col, Button, Form, Spinner, Alert,
  Toast, ToastContainer, Modal, Badge, Table
} from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import {
  collection, doc, setDoc, getDocs, query,
  where, orderBy, limit, onSnapshot, serverTimestamp,
  updateDoc, arrayUnion, writeBatch, getDoc, deleteDoc
} from "firebase/firestore";
import JoditEditor from "jodit-react";
import "jodit/es5/jodit.min.css";
import { useNavigate } from "react-router-dom";

import { db } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";

/* ---------- Constants ---------- */
const DIFFICULTY = ["Easy", "Medium", "Hard"];
const TYPE_OPTIONS = [
  { value: "mcq", label: "MCQ (Single Correct)" },
  { value: "multi_correct", label: "MCQ (Multi Correct)" },
  { value: "true_false", label: "True / False" },
  { value: "descriptive", label: "Descriptive" },
  { value: "match", label: "Match the Following" },
  { value: "assertion_reason", label: "Assertion & Reason" },
  { value: "statement_based", label: "Statement Based" },
  { value: "case_based", label: "Case Based" },
  { value: "mcq_descriptive", label: "MCQ + Descriptive" },
];

/* ---------- Class & Subject Maps ---------- */
const CLASS_OPTIONS = [
  ...Array.from({ length: 10 }, (_, i) => `Class ${i + 1}`),
  "Class 11 (Science)",
  "Class 11 (Commerce)",
  "Class 11 (Arts)",
  "Class 12 (Science)",
  "Class 12 (Commerce)",
  "Class 12 (Arts)",
  "Foundation 6",
  "Foundation 7",
  "Foundation 8",
  "Foundation 9",
  "Foundation 10",
  "NEET (Class 11)",
  "NEET (Class 12)",
  "NEET",
  "JEE (Main) Class 11",
  "JEE (Main) Class 12",
  "JEE (Main)",
  "JEE (Advanced)",
];

export const SUBJECTS_MAP = {
  "Class 1": ["English","Second Language","Mathematics","EVS","Computer Science","General Knowledge","Art","Physical Education"],
  "Class 2": ["English","Second Language","Mathematics","EVS","Computer Science","General Knowledge","Art","Physical Education"],
  "Class 3": ["English","Second Language","Mathematics","EVS","Computer Science","General Knowledge","Art","Physical Education"],
  "Class 4": ["English","Second Language","Mathematics","EVS","Computer Science","General Knowledge","Art","Physical Education"],
  "Class 5": ["English","Second Language","Mathematics","EVS","Computer Science","General Knowledge","Art","Physical Education"],

  "Class 6": ["English","Second Language","Mathematics","Science","Social Science","Computer Science","Sanskrit / Third Language","Physical Education","Art"],
  "Class 7": ["English","Second Language","Mathematics","Science","Social Science","Computer Science","Sanskrit / Third Language","Physical Education","Art"],
  "Class 8": ["English","Second Language","Mathematics","Science","Social Science","Computer Science","Sanskrit / Third Language","Physical Education","Art"],

  "Class 9": ["English","Second Language","Mathematics","Science (Physics/Chemistry/Biology)","Social Science (History/Geography/Civics/Economics)","Information Technology","Computer Applications","Physical Education","Art"],
  "Class 10": ["English","Second Language","Mathematics","Science (Physics/Chemistry/Biology)","Social Science (History/Geography/Civics/Economics)","Information Technology","Computer Applications","Physical Education","Art"],

  "Class 11 (Science)": ["English","Physics","Chemistry","Mathematics","Biology","Computer Science","Informatics Practices","Physical Education"],
  "Class 12 (Science)": ["English","Physics","Chemistry","Mathematics","Biology","Computer Science","Informatics Practices","Physical Education"],
  "Class 11 (Commerce)": ["English","Accountancy","Business Studies","Economics","Mathematics","Applied Mathematics","Informatics Practices","Computer Science"],
  "Class 12 (Commerce)": ["English","Accountancy","Business Studies","Economics","Mathematics","Applied Mathematics","Informatics Practices","Computer Science"],
  "Class 11 (Arts)": ["English","History","Geography","Political Science","Economics","Sociology","Psychology","Philosophy","Informatics Practices"],
  "Class 12 (Arts)": ["English","History","Geography","Political Science","Economics","Sociology","Psychology","Philosophy","Informatics Practices"],

  "Foundation 6": ["Physics Basics","Chemistry Basics","Biology Basics","Mathematics","Reasoning / Aptitude"],
  "Foundation 7": ["Physics Basics","Chemistry Basics","Biology Basics","Mathematics","Reasoning / Aptitude"],
  "Foundation 8": ["Physics Basics","Chemistry Basics","Biology Basics","Mathematics","Reasoning / Aptitude"],
  "Foundation 9": ["Physics Basics","Chemistry Basics","Biology Basics","Mathematics","Reasoning / Aptitude"],
  "Foundation 10": ["Physics Basics","Chemistry Basics","Biology Basics","Mathematics","Reasoning / Aptitude"],

  "NEET (Class 11)": ["Physics","Chemistry","Biology","Botany","Zoology","NCERT Module","Previous Year Questions"],
  "NEET (Class 12)": ["Physics","Chemistry","Biology","Botany","Zoology","NCERT Module","Previous Year Questions"],
  "NEET": ["Physics","Chemistry","Biology","Botany","Zoology","Full Syllabus Tests","Previous Year Questions"],

  "JEE (Main) Class 11": ["Physics","Chemistry","Mathematics","Aptitude / Reasoning","Previous Year Questions"],
  "JEE (Main) Class 12": ["Physics","Chemistry","Mathematics","Aptitude / Reasoning","Previous Year Questions"],
  "JEE (Main)": ["Physics","Chemistry","Mathematics","Full Syllabus Tests","Previous Year Questions"],

  "JEE (Advanced)": ["Physics (Advanced)","Chemistry (Advanced)","Mathematics (Advanced)","Advanced Problems","Previous Year Questions (Advanced)"],
};

/* ---------- Shared Question Bank ---------- */
const QB_COLLECTION = "questionbank";

/* ---------- HTML helpers ---------- */
const decodeHtml = (s = "") => {
  const el = document.createElement("textarea");
  el.innerHTML = s;
  return el.value;
};

const stripTagsAndNorm = (html = "") =>
  decodeHtml(String(html || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeHtml = (html = "") => stripTagsAndNorm(html).toLowerCase();

const stripHtml = (html = "") =>
  String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

/** DO NOT STRIP base64 images anymore */
const ALLOW_DATA_IMAGE_RE = /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=]+$/i;

const sanitizeHtml = (html = "") =>
  DOMPurify.sanitize(String(html || ""), {
    USE_PROFILES: { html: true },
    ADD_ATTR: ["target", "rel"],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i, // std safe
    // Custom hook: keep data:image/* base64
    BEFORE_SANITIZE_ELEMENTS: [],
    BEFORE_SANITIZE_ATTRIBUTES: [],
    // After sanitize pass: re-allow data images if stripped by default rule
    // (DOMPurify keeps data: by default in most versions, but we guard here)
  });

/** For Firestore store: keep as-is, just trim. We rely on DOMPurify only at render time. */
const cleanHtml = (html = "") => String(html || "").trim();

/* ---------- Fingerprint helpers ---------- */
const hash32 = (s = "") => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
};

const qbDocId = (fingerprint) => `qb_${hash32(fingerprint)}`;

// Fingerprint (special handling for "match"), with normalized meta
const buildFingerprint = (q) => {
  const type = q.questionType || "mcq";
  const stem =
    type === "case_based"
      ? normalizeHtml(q.passageHtml)
      : normalizeHtml(q.questionHtml);

  let optionsStr = "";
  if (type === "match") {
    optionsStr = (q.matchOptions || [])
      .map(arr => (arr || []).join(","))
      .join("|");
  } else {
    optionsStr = (q.options || [])
      .map((o) => normalizeHtml(o?.value || ""))
      .join("|");
  }

  const stmts = (q.statements || [])
    .map((s) => `${normalizeHtml(s.html)}:${s.isTrue ? "1" : "0"}`)
    .join("|");

  const caseSubs = (q.caseSubQs || [])
    .map((sq) => {
      const sqOpts = (sq.options || []).map((o) => normalizeHtml(o.value || "")).join(",");
      return `${normalizeHtml(sq.questionHtml)}::${sqOpts}::${sq.correctKey || ""}`;
    })
    .join("|");

  const parts = [
    String(q.subject || "").trim().toLowerCase(),
    String(q.chapter || "").trim().toLowerCase(),
    String(q.topic || "").trim().toLowerCase(),
    type,
    stem,
    optionsStr,
    String(q.matchPairCount || 0),
    stmts,
    caseSubs,
    q.trueFalseAnswer || "",
    q.arAnswer || "",
    normalizeHtml(q.descPartHtml || ""),
    String(q.correctOptionIndex ?? ""),
  ];
  return parts.join("||");
};

/* ---------- Small utils ---------- */
const randomId = () =>
  (typeof crypto !== "undefined" && crypto?.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

/** ---------- helpers (top-level, once) ---------- */
const approxDocBytes = (obj) => {
  try {
    return new Blob([JSON.stringify(obj)]).size;       // browser path
  } catch {
    return new TextEncoder().encode(JSON.stringify(obj)).length; // fallback
  }
};

/* ---------- Uncontrolled Jodit wrapper to avoid caret jump ---------- */
function JoditField({
  initialValue = "",
  config,
  onCommit,        // called on blur with HTML string
  onChange,        // optional live change
  heightOverride,  // optional
  seedKey = ""     // forces remount when changed
}) {
  const ref = useRef(null);
  const [init, setInit] = useState(initialValue);

  useEffect(() => {
    // Update default value on external reset.
    setInit(initialValue || "");
  }, [initialValue, seedKey]);

  const cfg = useMemo(() => ({
    ...config,
    height: heightOverride ?? config?.height ?? 220,
    // IMPORTANT: keep images as base64 if pasted
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: "insert_clear_html",
    pasteHTMLAction: "insert",
    uploader: { insertImageAsBase64URI: true },
  }), [config, heightOverride]);

  return (
    <JoditEditor
      key={seedKey}
      ref={ref}
      // Use defaultValue (uncontrolled) to keep caret where it is
      value={undefined}
      defaultValue={init}
      config={cfg}
      onChange={(html) => onChange?.(html)}
      onBlur={(html) => onCommit?.(html)}
    />
  );
}

export default function CreateQuestionBank() {
  const { schoolId, school, loading: schoolLoading } = useSchool();
 
  const nav = useNavigate();
  const { profile } = useAuth();

  const isActiveSchool = useMemo(() => {
    if (!school) return false;
    if (typeof school.active === "boolean") return school.active;
    const status = String(school.status || "").trim().toLowerCase();
    return status === "active";
  }, [school]);

  /* ---------- Suggestions (from shared bank) ---------- */
  const [suggest, setSuggest] = useState({ classes: [], subjects: [], chapters: [], topics: [] });
  useEffect(() => {
    if (!schoolId) return;

    const ref = collection(db, QB_COLLECTION);
    let fallbackUnsub = null;

    const unsub = onSnapshot(
      query(
        ref,
        where("sourceSchools", "array-contains", schoolId),
        orderBy("createdAt", "desc"),
        limit(800)
      ),
      (snap) => {
        const docs = snap.docs.map((d) => d.data() || {});
        const classes = new Set(docs.map(d => d.className || d.class || d.grade || d.classId).filter(Boolean));
        const subjects = new Set(docs.map(d => d.subject).filter(Boolean));
        const chapters = new Set(docs.map(d => d.chapter || d.chapterName || d.chapterNo).filter(Boolean));
        const topics = new Set(docs.map(d => d.topic).filter(Boolean));
        setSuggest({
          classes: Array.from(classes).sort(),
          subjects: Array.from(subjects).sort(),
          chapters: Array.from(chapters).sort((a,b) => String(a).localeCompare(String(b), undefined, { numeric:true })),
          topics: Array.from(topics).sort(),
        });
      },
      (err) => {
        if (err?.code === "failed-precondition") {
          // fallback listener without orderBy (no composite index needed)
          fallbackUnsub = onSnapshot(
            query(ref, where("sourceSchools", "array-contains", schoolId), limit(800)),
            (snap) => {
              const docs = snap.docs
                .map((d) => d.data() || {})
                .sort((a, b) => (b?.createdAt?.toMillis?.() ?? 0) - (a?.createdAt?.toMillis?.() ?? 0));
              const classes = new Set(docs.map(d => d.className || d.class || d.grade || d.classId).filter(Boolean));
              const subjects = new Set(docs.map(d => d.subject).filter(Boolean));
              const chapters = new Set(docs.map(d => d.chapter || d.chapterName || d.chapterNo).filter(Boolean));
              const topics = new Set(docs.map(d => d.topic).filter(Boolean));
              setSuggest({
                classes: Array.from(classes).sort(),
                subjects: Array.from(subjects).sort(),
                chapters: Array.from(chapters).sort((a,b) => String(a).localeCompare(String(b), undefined, { numeric:true })),
                topics: Array.from(topics).sort(),
              });
            }
          );
        } else {
          console.error("Suggest query failed:", err);
        }
      }
    );

    return () => {
      unsub && unsub();
      fallbackUnsub && fallbackUnsub();
    };
  }, [schoolId]);

  /* ---------- Toast ---------- */
  const [toast, setToast] = useState({ show: false, title: "", body: "" });
  const showToast = (title, body) => setToast({ show: true, title, body });

  /* ---------- Jodit configs (base) ---------- */
  const joditFull = (readOnly) => ({
    readonly: !!readOnly,
    height: 220,
    toolbar: true,
    toolbarAdaptive: true,
    toolbarSticky: false,
    // images are kept as base64; no stripping
    uploader: { insertImageAsBase64URI: true },
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
  });

  const joditMini = (readOnly) => ({
    readonly: !!readOnly,
    height: 140,
    toolbar: true,
    toolbarAdaptive: false,
    toolbarSticky: false,
    buttons: [
      "bold","italic","underline","|",
      "superscript","subscript","|",
      "align","|",
      "ul","ol","|",
      "link","image","symbol","|",
      "eraser"
    ].join(","),
    removeButtons: [
      "fullsize","source","print","preview","file","copyformat",
      "selectall","spellcheck","about"
    ],
    uploader: { insertImageAsBase64URI: true },
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
  });

  const [fullCfg, setFullCfg] = useState(joditFull(false));
  const [miniCfg, setMiniCfg] = useState(joditMini(false));
  useEffect(() => {
    setFullCfg(joditFull(!isActiveSchool));
    setMiniCfg(joditMini(!isActiveSchool));
  }, [isActiveSchool]);

  /* ---------- Form ---------- */
  const [editorSeed, setEditorSeed] = useState(0);

  // Meta
  const [className, setClassName] = useState("");
  const [subject, setSubject] = useState("");
  const [subjectCustom, setSubjectCustom] = useState("");
  const [chapter, setChapter] = useState("");
  const [topic, setTopic] = useState("");
  const [questionType, setQuestionType] = useState("mcq");
  const [difficulty, setDifficulty] = useState("Medium");

  // Content
  const [questionHtml, setQuestionHtml] = useState("");
  const [explanationHtml, setExplanationHtml] = useState("");

  // MCQ helpers
  const emptyOptionSet = () => ([
    { key: "A", value: "", isCorrect: false },
    { key: "B", value: "", isCorrect: false },
    { key: "C", value: "", isCorrect: false },
    { key: "D", value: "", isCorrect: false },
  ]);
  const [options, setOptions] = useState(emptyOptionSet());
  const [trueFalseAnswer, setTrueFalseAnswer] = useState("True");

  // Statement / Case
  const emptyStatementRow = (id = randomId()) => ({ id, key: "", html: "", isTrue: false });
  const [statements, setStatements] = useState([emptyStatementRow()]);
  const [passageHtml, setPassageHtml] = useState("");
  const emptyCaseSubQ = (id = randomId()) => ({ id, questionHtml: "", options: emptyOptionSet(), correctKey: "A" });
  const [caseSubQs, setCaseSubQs] = useState([emptyCaseSubQ()]);
  const [descPartHtml, setDescPartHtml] = useState("");

  // Assertion & Reason
  const [assertionHtml, setAssertionHtml] = useState("");
  const [reasonHtml, setReasonHtml] = useState("");
  const [arAnswer, setArAnswer] = useState("A");

  /* ---------- MATCH (labels-only) ---------- */
  const [matchCount, setMatchCount] = useState(4);
  const [matchOptions, setMatchOptions] = useState([[],[],[],[]]);
  const [correctMappingIdx, setCorrectMappingIdx] = useState(null);

  /* ---------- Bulk Upload ---------- */
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [, setBulkRows] = useState([]);
  const [bulkParsed, setBulkParsed] = useState([]); // {payload, fp, error}
  const [bulkReport, setBulkReport] = useState({ total: 0, created: 0, linked: 0, skipped: 0, errors: 0 });
  const [bulkProgress, setBulkProgress] = useState(0);
  const [stage, setStage] = useState([]); // [{id, index, payload, fp, error, selected}]

  const newId = () => Math.random().toString(36).slice(2);

  const rebuildStage = (parsed) => {
    const withIds = parsed.map((r, i) => ({
      id: newId(),
      index: r.index,
      payload: r.payload || null,
      fp: r.fp || null,
      error: r.error || null,
      selected: !r.error,
    }));
    setStage(withIds);
  };

  const toggleSelect = (id) =>
    setStage((rows) => rows.map(r => r.id === id ? { ...r, selected: !r.selected } : r));
  const removeRow = (id) => setStage((rows) => rows.filter(r => r.id !== id));
  const duplicateRow = (id) =>
    setStage((rows) => {
      const r = rows.find(x => x.id === id);
      if (!r) return rows;
      const copy = { ...r, id: newId(), index: r.index + " (copy)" };
      return [copy, ...rows];
    });
  const selectAll = (val) => setStage(rows => rows.map(r => ({ ...r, selected: val && !r.error })));

  const readFileAsArrayBuffer = (file) =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsArrayBuffer(file);
    });

  const S = (obj, key) => String(obj?.[key] ?? "").trim();
  const splitMulti = (s) => String(s || "").split("|").map(x => x.trim()).filter(Boolean);
  const yesNoToBool = (s) => /^true|t|yes|y|1$/i.test(String(s || "").trim());

  const parseMatchLine = (line = "", pairCount) => {
    const romans = ["i","ii","iii","iv","v","vi","vii","viii","ix","x","xi","xii","xiii","xiv","xv","xvi","xvii","xviii","xix","xx"];
    const map = Array.from({length: pairCount}, () => null);
    const parts = line.split(",").map(x => x.trim()).filter(Boolean);
    parts.forEach(p => {
      const m = p.match(/\(?([a-z]+)\)?\s*-\s*\(?([ivxlcdm]+)\)?/i);
      if (!m) return;
      const l = m[1].toLowerCase();
      const r = m[2].toLowerCase();
      const lIdx = l.charCodeAt(0) - 97;
      const rIdx = romans.indexOf(r);
      if (lIdx >= 0 && lIdx < pairCount && rIdx >= 0 && rIdx < pairCount) {
        map[lIdx] = rIdx;
      }
    });
    if (map.some(v => v == null)) return null;
    return map;
  };

  const leftLabel = (idx) => {
    let n = idx + 1, s = "";
    while (n > 0) { n--; s = String.fromCharCode(97 + (n % 26)) + s; n = Math.floor(n / 26); }
    return s;
  };
  const toRoman = (num) => {
 const map = [
  [1000, "m"],
  [900, "cm"],
  [500, "d"],
  [400, "cd"],
  [100, "c"],
  [90, "xc"],
  [50, "l"],
  [40, "xl"],
  [10, "x"],
  [9, "ix"],
  [5, "v"],
  [4, "iv"],
  [1, "i"],
];
    let res = "", n = Math.max(1, Math.floor(num));
    for (const [v,s] of map) while (n >= v) { res += s; n -= v; }
    return res;
  };
  const rightLabel = (idx) => toRoman(idx + 1);

  const formatOptionText = (arr = []) =>
    (!arr.length || arr.some(v => v == null))
      ? "— generate —"
      : arr.map((rIdx, lIdx) => `(${leftLabel(lIdx)})-(${rightLabel(rIdx)})`).join(", ");

  const shuffle = (n) => {
    const a = Array.from({ length: n }, (_, i) => i);
    for (let i = n - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    return a;
  };

  const buildMatchOptions = (n) => {
    if (n < 2) { showToast("Add pairs","Select at least 2 pairs."); return; }
    const opt1 = Array.from({ length: n }, (_, i) => i);
    const opts = [opt1];
    const seen = new Set([JSON.stringify(opt1)]);
    while (opts.length < 4) {
      let p = shuffle(n), guard = 0;
      while (seen.has(JSON.stringify(p)) && guard < 20) { p = shuffle(n); guard++; }
      opts.push(p); seen.add(JSON.stringify(p));
    }
    setMatchOptions(opts);
    setCorrectMappingIdx(0);
  };

  // Only when matchCount changes
  useEffect(() => { buildMatchOptions(matchCount); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [matchCount]);

  const subjectIsOther = subject === "__other__";
  const effectiveSubject = subjectIsOther ? subjectCustom.trim() : subject;

  /* ---------- helpers ---------- */
  const setCorrectSingle = (idx) => setOptions(opts => opts.map((o, i) => ({ ...o, isCorrect: i === idx })));
  const toggleCorrectMulti = (idx) => setOptions(opts => opts.map((o, i) => (i === idx ? { ...o, isCorrect: !o.isCorrect } : o)));

  const addStatement = () => { setStatements((s) => [...s, emptyStatementRow()]); showToast("Added","Statement added."); };
  const removeStatement = (id) => { setStatements((s) => s.length > 1 ? s.filter(x => x.id !== id) : s); showToast("Removed","Statement removed."); };

  const addCaseSubQ = () => { setCaseSubQs((arr) => [...arr, emptyCaseSubQ()]); showToast("Added","Sub-question added."); };
  const removeCaseSubQ = (id) => { setCaseSubQs((arr) => arr.length > 1 ? arr.filter(x => x.id !== id) : arr); showToast("Removed","Sub-question removed."); };

  /** Reset helpers */
  const softResetContent = () => {
    setQuestionHtml("");
    setExplanationHtml("");
    setOptions(emptyOptionSet());
    setTrueFalseAnswer("True");
    setStatements([emptyStatementRow()]);
    setPassageHtml("");
    setCaseSubQs([emptyCaseSubQ()]);
    setDescPartHtml("");
    setAssertionHtml("");
    setReasonHtml("");
    setArAnswer("A");
    setMatchCount(4);
    setMatchOptions([[],[],[],[]]);
    setCorrectMappingIdx(null);
    setEditorSeed((x) => x + 1); // force re-mount editors cleanly
  };

  const hardResetAll = () => {
    setClassName("");
    setSubject("");
    setSubjectCustom("");
    setChapter("");
    setTopic("");
    setQuestionType("mcq");
    setDifficulty("Medium");
    softResetContent();
    showToast("Cleared","Form reset.");
  };

  /* ---------- Validation ---------- */
  const validateByType = () => {
    if (!className.trim()) { showToast("Missing","Please select a Class."); return false; }
    if (!String(effectiveSubject || "").trim()) { showToast("Missing","Please select a Subject."); return false; }
    if (!chapter.trim()) { showToast("Missing","Chapter is required."); return false; }
    if (!topic.trim()) { showToast("Missing","Topic is required."); return false; }

    const hasStem = questionType === "case_based"
      ? (passageHtml || questionHtml)
      : (questionHtml || /<img/i.test(questionHtml));
    if (!hasStem && questionType !== "case_based") { showToast("Missing","Question content cannot be empty."); return false; }

    if (questionType === "mcq" && options.filter(o => o.isCorrect).length !== 1) {
      showToast("Invalid","Single-correct MCQ must have exactly one correct option."); return false;
    }
    if (questionType === "multi_correct" && !options.some(o => o.isCorrect)) {
      showToast("Invalid","Multi-correct MCQ needs at least one correct option."); return false;
    }
    if (questionType === "true_false" && !["True","False"].includes(trueFalseAnswer)) {
      showToast("Invalid","Pick True or False."); return false;
    }
    if (questionType === "match") {
      if (matchCount < 2) { showToast("Invalid","Select at least 2 pairs."); return false; }
      const ok = matchOptions.length === 4 &&
                 matchOptions.every(o => o.length === matchCount && o.every(v => v !== null && v !== undefined));
      if (!ok) { showToast("Invalid","Click Regenerate Options."); return false; }
      if (correctMappingIdx == null) { showToast("Invalid","Select the correct option."); return false; }
    }
    if (questionType === "assertion_reason") {
      if (!assertionHtml || !reasonHtml) { showToast("Invalid","Provide both Assertion and Reason."); return false; }
      if (!["A","B","C","D"].includes(arAnswer)) { showToast("Invalid","Pick a valid A/R answer (A/B/C/D)."); return false; }
    }
    if (questionType === "statement_based" && !statements.some(s => s.html)) {
      showToast("Invalid","Add at least one statement."); return false;
    }
    if (questionType === "case_based") {
      if (!passageHtml) { showToast("Invalid","Provide the case/passage."); return false; }
      const validSubs = caseSubQs.filter(sq => sq.questionHtml && ["A","B","C","D"].includes(sq.correctKey));
      if (validSubs.length === 0) { showToast("Invalid","Add at least one valid sub-question."); return false; }
    }
    if (questionType === "mcq_descriptive") {
      if (options.filter(o => o.isCorrect).length !== 1) { showToast("Invalid","MCQ part must have exactly one correct option."); return false; }
      if (!descPartHtml) { showToast("Invalid","Provide the descriptive part."); return false; }
    }
    return true;
  };

  /* ---------- Preview ---------- */
  const [showPreview, setShowPreview] = useState(false);
  const PreviewBlock = () => (
    <div>
      <div className="mb-2">
        <Badge bg="primary" className="me-2">{className || "Class"}</Badge>
        <Badge bg="secondary" className="me-2">{effectiveSubject || "Subject"}</Badge>
        <Badge bg="info" text="dark" className="me-2">{difficulty}</Badge>
        <Badge bg="light" text="dark" className="me-2">{chapter || "Chapter"}</Badge>
        <Badge bg="light" text="dark">{topic || "Topic"}</Badge>
      </div>

      {questionType !== "case_based" && (
        <div
          className="border rounded p-2 mb-3"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(questionHtml || "<i>Question…</i>") }}
        />
      )}

      {["mcq","multi_correct","mcq_descriptive"].includes(questionType) && (
        <div className="mb-3">
          {options.map((o) => (
            <div key={o.key} className="d-flex align-items-start gap-2 mb-2">
              <div className="fw-semibold">{o.key}.</div>
              <div
                className="flex-grow-1"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(o.value || "<i>—</i>") }}
              />
              {o.isCorrect && <Badge bg="success">Correct</Badge>}
            </div>
          ))}
          {questionType === "mcq_descriptive" && (
            <div
              className="mt-2 border rounded p-2"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(descPartHtml || "<i>Descriptive part…</i>") }}
            />
          )}
        </div>
      )}

      {questionType === "true_false" && (
        <div className="mb-3">Answer: <Badge bg="dark">{trueFalseAnswer}</Badge></div>
      )}

      {questionType === "match" && (
        <>
          <div className="mb-1 text-muted">Pairs: {matchCount} (labels: a… / i…)</div>
          <div className="mb-2 fw-semibold">Answer Options</div>
          {[0,1,2,3].map((idx) => (
            <div key={idx} className="d-flex align-items-center gap-2 mb-1">
              <div><Badge bg={correctMappingIdx===idx ? "success" : "secondary"}>Option {idx+1}</Badge></div>
              <span className="text-muted small">{formatOptionText(matchOptions[idx])}</span>
            </div>
          ))}
        </>
      )}

      {questionType === "assertion_reason" && (
        <>
          <div
            className="border rounded p-2 mb-2"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(assertionHtml || "<i>Assertion…</i>") }}
          />
          <div
            className="border rounded p-2 mb-2"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(reasonHtml || "<i>Reason…</i>") }}
          />
          <div>Answer: <Badge bg="dark">{arAnswer}</Badge></div>
        </>
      )}

      {questionType === "statement_based" && (
        <div className="mb-3">
          {statements.map((s, i) => (
            <div key={s.id} className="d-flex gap-2 align-items-start mb-2">
              <div className="fw-semibold">{(s.key || String.fromCharCode(65+i))}.</div>
              <div
                className="flex-grow-1"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.html || "<i>—</i>") }}
              />
              <Badge bg={s.isTrue ? "success" : "danger"}>{s.isTrue ? "True" : "False"}</Badge>
            </div>
          ))}
        </div>
      )}

      {questionType === "case_based" && (
        <>
          <div
            className="border rounded p-2 mb-3"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(passageHtml || "<i>Passage…</i>") }}
          />
          {caseSubQs.map((sq, idx) => (
            <Card className="mb-3" key={sq.id}>
              <Card.Body>
                <div className="mb-2 fw-semibold">Q{idx+1}</div>
                <div
                  className="border rounded p-2 mb-2"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(sq.questionHtml || "<i>—</i>") }}
                />
                {sq.options.map((o) => (
                  <div key={o.key} className="d-flex align-items-start gap-2 mb-1">
                    <div className="fw-semibold">{o.key}.</div>
                    <div
                      className="flex-grow-1"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(o.value || "<i>—</i>") }}
                    />
                    {sq.correctKey === o.key && <Badge bg="success">Correct</Badge>}
                  </div>
                ))}
              </Card.Body>
            </Card>
          ))}
        </>
      )}

      {explanationHtml && (
        <div className="mt-3">
          <div className="fw-semibold mb-1">Explanation</div>
          <div
            className="border rounded p-2"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(explanationHtml) }}
          />
        </div>
      )}
    </div>
  );

  /* ---------- Save & Analyze (shared bank) ---------- */
  const [saving, setSaving] = useState(false);

  const basePayload = () => {
    const cleanOptions = (arr = []) =>
      (arr || []).map(o => ({ ...o, value: cleanHtml(o?.value || "") }));

    const cleanStatements = (arr = []) =>
      (arr || []).map(s => ({ ...s, html: cleanHtml(s?.html || "") }));

    const cleanCaseSubQs = (arr = []) =>
      (arr || [])
        .map(sq => ({
          id: sq.id,
          questionHtml: cleanHtml(sq?.questionHtml || ""),
          options: cleanOptions(sq?.options || []),
          correctKey: sq.correctKey
        }))
        .filter(sq => sq.questionHtml);

    let typeData = {};
    switch (questionType) {
      case "mcq": typeData = { options: cleanOptions(options), singleCorrect: true }; break;
      case "multi_correct": typeData = { options: cleanOptions(options), singleCorrect: false }; break;
      case "true_false": typeData = { trueFalseAnswer }; break;
      case "descriptive": typeData = {}; break;
      case "match": {
        typeData = {
          matchPairCount: matchCount,
          labelScheme: { left: "alpha", right: "roman" },
          matchOptions: matchOptions.map(o => o.slice(0, matchCount)),
          correctOptionIndex: correctMappingIdx,
        };
        break;
      }
      case "assertion_reason": typeData = {
        assertionHtml: cleanHtml(assertionHtml),
        reasonHtml: cleanHtml(reasonHtml),
        arAnswer
      }; break;
      case "statement_based": typeData = { statements: cleanStatements(statements) }; break;
      case "case_based":
        typeData = { passageHtml: cleanHtml(passageHtml), caseSubQs: cleanCaseSubQs(caseSubQs) };
        break;
      case "mcq_descriptive":
        typeData = { options: cleanOptions(options), singleCorrect: true, descPartHtml: cleanHtml(descPartHtml) };
        break;
      default: break;
    }
    return {
      className: className.trim(),
      subject: String((subject === "__other__" ? subjectCustom : subject) || "").trim(),
      chapter: chapter.trim(),
      topic: topic.trim(),
      questionType,
      difficulty,
      questionHtml: cleanHtml(questionHtml),
      explanationHtml: cleanHtml(explanationHtml),
      templateId: "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...typeData,
    };
  };

  /* ---------- Analyze duplicate ---------- */
  const analyzeDuplicate = async () => {
    if (!schoolId) return;
    if (!validateByType()) return;

    const base = basePayload();
    const fp = buildFingerprint(base);

    const qsRef = collection(db, QB_COLLECTION);
    const dupSnap = await getDocs(query(qsRef, where("dedupeKey", "==", fp), limit(1)));

    if (!dupSnap.empty) {
      const data = dupSnap.docs[0].data() || {};
      const owner = data.ownerSchoolId || (data.sourceSchools?.[0]) || "another school";
      showToast("Already in Question Bank", `This question already exists (added by ${owner}).`);
      return;
    }
    showToast("Looks good", "No duplicates in the shared bank. You can save.");
  };

  // Recursively remove undefined for Firestore
  const deepSanitize = (val) => {
    if (val === undefined) return null;
    if (val === null) return null;

    if (typeof val === "string") return val;
    if (typeof val === "number" || typeof val === "boolean") return val;

    if (Array.isArray(val)) {
      return val
        .filter((v) => v !== undefined)
        .map((v) => deepSanitize(v));
    }

    if (typeof val === "object") {
      const out = {};
      for (const k of Object.keys(val)) {
        const v = val[k];
        if (v === undefined) continue;
        out[k] = deepSanitize(v);
      }
      return out;
    }

    return val;
  };

  /* ---------- Save (create or link existing) ---------- */
  const save = async () => {
    if (!isActiveSchool) return showToast("Disabled", "School is inactive.");
    if (!schoolId) return showToast("Missing context", "No school selected in this page.");
    if (!profile?.schoolId) return showToast("No school on account", "Your account isn’t linked to any school.");
    if (profile.schoolId !== schoolId) {
      console.warn("School mismatch", { uiSchoolId: schoolId, profileSchoolId: profile.schoolId });
      return showToast(
        "School mismatch",
        `This page is set to ${schoolId}, but your account belongs to ${profile.schoolId}.`
      );
    }

    if (!validateByType()) return;

    const base = basePayload();
    const fp = buildFingerprint(base);

    try {
      setSaving(true);

      const qsRef = collection(db, QB_COLLECTION);
      const dupSnap = await getDocs(query(qsRef, where("dedupeKey", "==", fp), limit(1)));

      if (!dupSnap.empty) {
        // link existing
        const dupRef = dupSnap.docs[0].ref;
        await updateDoc(dupRef, {
          sourceSchools: arrayUnion(profile.schoolId),
          updatedAt: serverTimestamp(),
          linkedBy: {
            schoolId: profile.schoolId,
            at: serverTimestamp(),
          },
        });
        showToast("Already in Bank", "Linked the existing question to your school.");
        softResetContent();
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      // create new
      const targetRef = doc(db, QB_COLLECTION, qbDocId(fp));
      const rawDoc = {
        ...base,
        ownerSchoolId: profile.schoolId,
        sourceSchools: [profile.schoolId],
        dedupeKey: fp,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const requiredString = (s) => typeof s === "string" && s.trim().length > 0;
      const okShape =
        requiredString(rawDoc.ownerSchoolId) &&
        Array.isArray(rawDoc.sourceSchools) &&
        rawDoc.sourceSchools.length === 1 &&
        rawDoc.sourceSchools[0] === profile.schoolId &&
        requiredString(rawDoc.dedupeKey) &&
        ["mcq","multi_correct","true_false","descriptive","match","assertion_reason","statement_based","case_based","mcq_descriptive"]
          .includes(rawDoc.questionType) &&
        ["Easy","Medium","Hard"].includes(rawDoc.difficulty) &&
        requiredString(rawDoc.subject) &&
        requiredString(rawDoc.chapter) &&
        requiredString(rawDoc.topic) &&
        (rawDoc.questionType === "case_based" || requiredString(rawDoc.questionHtml));

      if (!okShape) {
        console.error("Preflight shape check failed:", rawDoc);
        showToast("Save failed", "Payload shape didn’t satisfy security rules.");
        setSaving(false);
        return;
      }

      const docToWrite = deepSanitize(rawDoc);

      // Size guard (warn if very big HTML with base64 images)
      const bytes = approxDocBytes({
        ...docToWrite,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      if (bytes > 900 * 1024) {
        showToast("Too large", "This question (HTML + images) is too big. Remove or compress embedded images.");
        setSaving(false);
        return;
      }

      await setDoc(targetRef, docToWrite, { merge: false });

      showToast("Saved", "Question added to the shared bank.");
      softResetContent();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error("Firestore create failed:", e?.code, e?.message, e);
      showToast("Save failed", e?.message || "Could not save question.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- XLSX/CSV parsing ---------- */
  const parseWorkbook = (wb) => {
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
    return rows;
  };

  const rowToPayload = (row) => {
    const cls = S(row, "class");
    const subj = S(row, "subject");
    const chap = S(row, "chapter");
    const topi = S(row, "topic");
    const diff = S(row, "difficulty") || "Medium";
    const qtype = S(row, "question_type").toLowerCase();

    if (!cls || !subj || !chap || !topi || !qtype) {
      return { error: "Missing one of: class/subject/chapter/topic/question_type" };
    }

    const common = {
      className: cls,
      subject: subj,
      chapter: chap,
      topic: topi,
      difficulty: diff,
    };

    const letterIndex = (k) => {
      const m = String(k||"").trim().toUpperCase();
      const idx = ["A","B","C","D"].indexOf(m);
      return idx >= 0 ? idx : null;
    };

    const optFromRow = (row, key) => S(row, key) || "";

    const stem = S(row, "question_html");
    const explanation = S(row, "explanation_html");

    switch (qtype) {
      case "mcq": {
        const correct = letterIndex(S(row, "correct"));
        if (correct == null) return { error: "MCQ 'correct' must be A/B/C/D" };
        return {
          ...common,
          questionType: "mcq",
          questionHtml: stem,
          explanationHtml: explanation,
          options: [
            { key: "A", value: optFromRow(row,"option_a"), isCorrect: correct===0 },
            { key: "B", value: optFromRow(row,"option_b"), isCorrect: correct===1 },
            { key: "C", value: optFromRow(row,"option_c"), isCorrect: correct===2 },
            { key: "D", value: optFromRow(row,"option_d"), isCorrect: correct===3 },
          ],
          singleCorrect: true,
        };
      }

      case "multi_correct": {
        const list = splitMulti(S(row, "correct_multi")).map(letterIndex).filter(x => x!=null);
        if (!list.length) return { error: "Multi-correct needs at least one correct option in 'correct_multi' (A|C...)" };
        return {
          ...common,
          questionType: "multi_correct",
          questionHtml: stem,
          explanationHtml: explanation,
          options: [
            { key: "A", value: optFromRow(row,"option_a"), isCorrect: list.includes(0) },
            { key: "B", value: optFromRow(row,"option_b"), isCorrect: list.includes(1) },
            { key: "C", value: optFromRow(row,"option_c"), isCorrect: list.includes(2) },
            { key: "D", value: optFromRow(row,"option_d"), isCorrect: list.includes(3) },
          ],
          singleCorrect: false,
        };
      }

      case "true_false": {
        const ans = S(row, "tf_answer");
        if (!/^true|false$/i.test(ans)) return { error: "TF must have tf_answer True/False" };
        return {
          ...common,
          questionType: "true_false",
          questionHtml: stem,
          trueFalseAnswer: /^true$/i.test(ans) ? "True" : "False",
          explanationHtml: explanation,
        };
      }

      case "descriptive": {
        return {
          ...common,
          questionType: "descriptive",
          questionHtml: stem,
          explanationHtml: explanation,
        };
      }

      case "mcq_descriptive": {
        const correct = letterIndex(S(row, "correct"));
        if (correct == null) return { error: "MCQ+Desc 'correct' must be A/B/C/D" };
        const descHtml = S(row, "desc_html");
        if (!descHtml) return { error: "MCQ+Desc needs desc_html" };
        return {
          ...common,
          questionType: "mcq_descriptive",
          questionHtml: stem,
          explanationHtml: explanation,
          descPartHtml: descHtml,
          options: [
            { key: "A", value: optFromRow(row,"option_a"), isCorrect: correct===0 },
            { key: "B", value: optFromRow(row,"option_b"), isCorrect: correct===1 },
            { key: "C", value: optFromRow(row,"option_c"), isCorrect: correct===2 },
            { key: "D", value: optFromRow(row,"option_d"), isCorrect: correct===3 },
          ],
          singleCorrect: true,
        };
      }

      case "assertion_reason": {
        const ar = S(row, "ar_answer").toUpperCase();
        if (!["A","B","C","D"].includes(ar)) return { error: "A/R needs ar_answer as A/B/C/D" };
        return {
          ...common,
          questionType: "assertion_reason",
          assertionHtml: S(row,"assertion_html"),
          reasonHtml: S(row,"reason_html"),
          arAnswer: ar,
          questionHtml: "",
          explanationHtml: explanation,
        };
      }

      case "statement_based": {
        const keys = splitMulti(S(row, "statement_keys"));
        const htmls = splitMulti(S(row, "statement_htmls"));
        const tf = splitMulti(S(row, "statement_true"));
        if (!htmls.length) return { error: "Statement-based needs statement_htmls like '<p>..</p>|<p>..</p>'" };
        const stmts = htmls.map((h, i) => ({
          id: randomId(),
          key: (keys[i] || "").toUpperCase().slice(0,1),
          html: h,
          isTrue: yesNoToBool(tf[i] || "0"),
        }));
        return {
          ...common,
          questionType: "statement_based",
          questionHtml: stem,
          statements: stmts,
          explanationHtml: explanation,
        };
      }

      case "match": {
        const cnt = parseInt(S(row, "pair_count") || "0", 10);
        if (!(cnt >= 2)) return { error: "Match needs pair_count >= 2" };
        const o1 = parseMatchLine(S(row,"option1_map"), cnt);
        const o2 = parseMatchLine(S(row,"option2_map"), cnt);
        const o3 = parseMatchLine(S(row,"option3_map"), cnt);
        const o4 = parseMatchLine(S(row,"option4_map"), cnt);
        if (!o1 || !o2 || !o3 || !o4) return { error: "Invalid option*_map format; use 'a-i,b-ii,c-iii,...'" };
        const corr = parseInt(S(row, "correct_option"), 10);
        if (![1,2,3,4].includes(corr)) return { error: "correct_option must be 1/2/3/4" };
        return {
          ...common,
          questionType: "match",
          questionHtml: stem,
          explanationHtml: explanation,
          matchPairCount: cnt,
          labelScheme: { left: "alpha", right: "roman" },
          matchOptions: [o1, o2, o3, o4],
          correctOptionIndex: corr - 1,
        };
      }

      case "case_based": {
        const passage = S(row, "passage_html");
        if (!passage) return { error: "Case-based needs passage_html" };
        const mkSub = (n) => {
          const q = S(row, `q${n}_html`);
          if (!q) return null;
          const c = S(row, `q${n}_correct`).toUpperCase();
          if (!["A","B","C","D"].includes(c)) return { error: `q${n}_correct must be A/B/C/D` };
          return {
            id: randomId(),
            questionHtml: q,
            correctKey: c,
            options: [
              { key: "A", value: S(row, `q${n}_a`), isCorrect: c==="A" },
              { key: "B", value: S(row, `q${n}_b`), isCorrect: c==="B" },
              { key: "C", value: S(row, `q${n}_c`), isCorrect: c==="C" },
              { key: "D", value: S(row, `q${n}_d`), isCorrect: c==="D" },
            ],
          };
        };
        const subs = [mkSub(1), mkSub(2), mkSub(3)].filter(Boolean);
        if (!subs.length) return { error: "Case-based needs at least one sub-question (q1_*)" };
        return {
          ...common,
          questionType: "case_based",
          passageHtml: passage,
          caseSubQs: subs,
          questionHtml: "",
          explanationHtml: explanation,
        };
      }

      default:
        return { error: `Unsupported question_type: ${qtype}` };
    }
  };

  const loadBulkFile = async (file) => {
    const ab = await readFileAsArrayBuffer(file);
    const wb = XLSX.read(ab, { type: "array" });
    const rows = parseWorkbook(wb);

    const parsed = rows.map((r, idx) => {
      const out = rowToPayload(r);
      if (out.error) return { index: idx + 1, error: out.error };

      const payload = {
        ...out,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        templateId: "",
        explanationHtml: out.explanationHtml || "",
      };
      const fp = buildFingerprint(payload);
      return { index: idx + 1, payload, fp };
    });

    setBulkRows(rows);
    setBulkParsed(parsed);
    rebuildStage(parsed);
  };

  /* ---------- Bulk commit (create/link) ---------- */
  const commitBulk = async () => {
    if (!isActiveSchool) { showToast("Disabled", "School is inactive."); return; }
    if (!schoolId) return;
    if (!profile?.schoolId) { showToast("No school on account", "Your account isn’t linked to any school."); return; }
  if (profile.schoolId !== schoolId) {
    showToast("School mismatch", `This page is set to ${schoolId}, but your account belongs to ${profile.schoolId}.`);
    return;
 }

    const candidates = (stage || []).filter(r => r.selected && !r.error && r.payload);
    const total = candidates.length;
    if (!total) { showToast("No rows", "Nothing selected to upload."); return; }

    setBulkBusy(true);
    setBulkProgress(0);

    let created = 0, linked = 0, skipped = 0, errors = 0;

    const step = () => setBulkProgress(p => Math.min(100, p + (100 / Math.max(1, total))));
    const sleep = (ms) => new Promise(res => setTimeout(res, ms));
    const commitWithRetry = async (fn, tries = 5) => {
      for (let i = 0; i < tries; i++) {
        try { return await fn(); }
        catch (e) {
          const code = e?.code || "";
          if (["aborted", "unavailable", "deadline-exceeded"].includes(code) && i < tries - 1) {
            const backoff = 200 * Math.pow(2, i) + Math.floor(Math.random() * 200);
            await sleep(backoff);
            continue;
          }
          throw e;
        }
      }
    };

    try {
      const READ_CHUNK = 500;
      const WRITE_CHUNK = 400;

      for (let i = 0; i < total; i += READ_CHUNK) {
        const slice = candidates.slice(i, i + READ_CHUNK);

        const items = slice.map(item => {
          const fp = item.fp || buildFingerprint(item.payload);
          const id = qbDocId(fp);
          return { ...item, fp, docId: id, ref: doc(db, QB_COLLECTION, id) };
        });

        // read existing
        const snaps = await Promise.allSettled(items.map(x => getDoc(x.ref)));
        const annotated = snaps.map((s, idx) => {
          if (s.status !== "fulfilled") {
            return { ...items[idx], _skip: true, _readError: s.reason?.message || "read failed" };
          }
          const snap = s.value;
          return { ...items[idx], exists: snap.exists(), existing: snap.exists() ? (snap.data() || {}) : null };
        });

        const readErrors = annotated.reduce((acc, it) => acc + (it._skip ? 1 : 0), 0);
        errors += readErrors;
        for (let k = 0; k < readErrors; k++) step();

        // write in chunks
        for (let j = 0; j < annotated.length; j += WRITE_CHUNK) {
          const part = annotated.slice(j, j + WRITE_CHUNK);
          const batch = writeBatch(db);

          for (const it of part) {
            if (it._skip || !it.payload) { step(); continue; }

            try {
              if (it.exists) {
               const already = Array.isArray(it.existing?.sourceSchools) && it.existing.sourceSchools.includes(schoolId);
     if (already) {
       skipped++;
     } else {
       // If rules aren’t updated yet, you can skip linking others’ docs to avoid failures:
       const owner = it.existing?.ownerSchoolId;
       if (owner && owner !== schoolId) {
         // TEMP: skip until rules support safe linking
         skipped++;
       } else {
         // owner is same school – ok to update
         batch.update(it.ref, {
           sourceSchools: arrayUnion(schoolId),
           updatedAt: serverTimestamp(),
           linkedBy: { schoolId, at: serverTimestamp() },
         });
         linked++;
       }
     }
              } else {
                // create new
                batch.set(it.ref, {
                  ...it.payload,
                  ownerSchoolId: schoolId,
                  sourceSchools: [schoolId],
                  dedupeKey: it.fp,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                });
                created++;
              }
            } catch {
              errors++;
            } finally {
              step();
            }
          }

          await commitWithRetry(() => batch.commit());
        }
      }

      setBulkReport({ total, created, linked, skipped, errors });
      showToast("Bulk Upload Complete", `Created: ${created}, Linked: ${linked}, Skipped: ${skipped}, Errors: ${errors}`);
    } catch (e) {
      setBulkReport(r => ({ ...r, error: e?.message || String(e) }));
      showToast("Bulk Failed", e?.message || "See console");
    } finally {
      setBulkBusy(false);
    }
  };

  /* ---------- Download template helper ---------- */
  const downloadText = (filename, text) => {
    const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const templates = {
    mcq: `class,subject,chapter,topic,difficulty,question_type,question_html,option_a,option_b,option_c,option_d,correct,explanation_html
Class 10,Science,2 - Acids,Bases,Medium,mcq,<p>Which is a base?</p>,<p>HCl</p>,<p>NaOH</p>,<p>CO2</p>,<p>SO2</p>,B,<p>Alkali metal hydroxide</p>
`,
    multi_correct: `class,subject,chapter,topic,difficulty,question_type,question_html,option_a,option_b,option_c,option_d,correct_multi,explanation_html
Class 10,Science,Electricity,Ohm's Law,Hard,multi_correct,<p>Select true statements</p>,<p>V∝I</p>,<p>R constant</p>,<p>P=VI</p>,<p>Q=nIt</p>,A|C,<p>Ohm & power</p>
`,
    true_false: `class,subject,chapter,topic,difficulty,question_type,question_html,tf_answer,explanation_html
Class 9,Science,Motion,Kinematics,Easy,true_false,<p>Displacement is scalar.</p>,False,<p>It is vector</p>
`,
    descriptive: `class,subject,chapter,topic,difficulty,question_type,question_html,explanation_html
Class 12,Physics,Optics,Interference,Medium,descriptive,<p>Explain Young's experiment.</p>,<p>—</p>
`,
    mcq_descriptive: `class,subject,chapter,topic,difficulty,question_type,question_html,option_a,option_b,option_c,option_d,correct,desc_html,explanation_html
Class 11,Chemistry,Stoichiometry,Mole concept,Medium,mcq_descriptive,<p>1 mole equals?</p>,<p>6.022e23 entities</p>,<p>1 g</p>,<p>22.4 L</p>,<p>—</p>,A,<p>Then solve a brief numerical…</p>,<p>—</p>
`,
    assertion_reason: `class,subject,chapter,topic,difficulty,question_type,assertion_html,reason_html,ar_answer,explanation_html
NEET,Physics,Electrostatics,Gauss Law,Medium,assertion_reason,<p>Flux depends on surface area.</p>,<p>Because Gauss law…</p>,B,<p>—</p>
`,
    statement_based: `class,subject,chapter,topic,difficulty,question_type,question_html,statement_keys,statement_htmls,statement_true,explanation_html
JEE (Main),Physics,Heat,Calorimetry,Medium,statement_based,<p>Choose the correct statements</p>,A|B|C,"<p>Ice at 0°C has latent heat</p>|<p>Steam has zero latent heat</p>|<p>Water has Cp</p>",1|0|1,<p>—</p>
`,
    match: `class,subject,chapter,topic,difficulty,question_type,question_html,pair_count,option1_map,option2_map,option3_map,option4_map,correct_option,explanation_html
Class 12,Biology,Classification,Taxonomy,Medium,match,<p>Match List-I with List-II</p>,4,"a-i,b-ii,c-iii,d-iv","a-ii,b-i,c-iv,d-iii","a-iii,b-iv,c-i,d-ii","a-iv,b-iii,c-ii,d-i",1,<p>—</p>
`,
    case_based: `class,subject,chapter,topic,difficulty,question_type,passage_html,q1_html,q1_a,q1_b,q1_c,q1_d,q1_correct,q2_html,q2_a,q2_b,q2_c,q2_d,q2_correct,explanation_html
NEET (Class 12),Biology,Human Physiology,Blood,Medium,case_based,"<p>Read the passage on blood groups…</p>","<p>ABO system depends on?</p>","<p>Antigens</p>","<p>Antibodies</p>","<p>Rh factor</p>","<p>Enzymes</p>",A,"<p>Universal donor?</p>","<p>O+</p>","<p>O-</p>","<p>AB+</p>","<p>A-</p>",B,<p>—</p>
`,
  };

  const downloadTemplate = (key) => {
    if (!templates[key]) return;
    downloadText(`${key}_template.csv`, templates[key]);
  };

  /* ---------- MY UPLOADS (view & CRUD own data) ---------- */
  const [showMine, setShowMine] = useState(false);
  const [myBank, setMyBank] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
    const canCrud = (q) => q?.ownerSchoolId && q.ownerSchoolId === schoolId;

// ==== Bulk select/delete state & helpers ====
const [selectedIds, setSelectedIds] = useState(new Set());
const [bulkDeleting, setBulkDeleting] = useState(false);

// Keep selection valid when list changes (or school changes)
useEffect(() => {
  setSelectedIds(prev => {
    const next = new Set();
    const validIds = new Set(myBank.filter(canCrud).map(q => q.id));
    for (const id of prev) if (validIds.has(id)) next.add(id);
    return next;
  });
}, [myBank, schoolId]);

const selectableIds = useMemo(
  () => myBank.filter(canCrud).map(q => q.id),
  [myBank, schoolId]
);

const allSelected =
  selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));

const toggleRow = (id) =>
  setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

const toggleSelectAll = () =>
  setSelectedIds(prev => {
    if (allSelected) {
      const next = new Set(prev);
      selectableIds.forEach(id => next.delete(id));
      return next;
    } else {
      const next = new Set(prev);
      selectableIds.forEach(id => next.add(id));
      return next;
    }
  });

const bulkDelete = async () => {
  const ids = Array.from(selectedIds);
  if (!ids.length) return;
  try {
    setBulkDeleting(true);
    const batch = writeBatch(db);
    ids.forEach(id => batch.delete(doc(db, QB_COLLECTION, id)));
    await batch.commit();
    showToast("Deleted", `Removed ${ids.length} question(s).`);
    setSelectedIds(new Set());
  } catch (e) {
    showToast("Bulk delete failed", e?.message || "Could not delete selected.");
  } finally {
    setBulkDeleting(false);
  }
};


  useEffect(() => {
    if (!schoolId || !showMine) return;
    setMyLoading(true);
    const ref = collection(db, QB_COLLECTION);
    const qy = query(ref, where("ownerSchoolId", "==", schoolId), orderBy("updatedAt", "desc"), limit(500));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        setMyBank(snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) })));
        setMyLoading(false);
      },
      () => setMyLoading(false)
    );
    return () => unsub();
  }, [schoolId, showMine]);


  // Modals for My Uploads
  const [viewQ, setViewQ] = useState(null);
  const [editQ, setEditQ] = useState(null);
  const [editData, setEditData] = useState({ subject:"", chapter:"", topic:"", difficulty:"Medium", marks:"", negativeMarks:"" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const openView = (q) => setViewQ(q);
  const openEdit = (q) => {
    if (!canCrud(q)) return;
    setEditQ(q);
    setEditData({
      subject: q.subject || "",
      chapter: q.chapter || q.chapterName || q.chapterNo || "",
      topic: q.topic || "",
      difficulty: q.difficulty || "Medium",
      marks: typeof q.marks === "number" ? q.marks : "",
      negativeMarks: typeof q.negativeMarks === "number" ? q.negativeMarks : "",
    });
  };

  const saveEdit = async () => {
    if (!editQ) return;
    try {
      setSavingEdit(true);
      const ref = doc(db, QB_COLLECTION, editQ.id);
      await updateDoc(ref, {
        subject: String(editData.subject || "").trim(),
        chapter: String(editData.chapter || "").trim(),
        topic: String(editData.topic || "").trim(),
        difficulty: editData.difficulty || "Medium",
        ...(editData.marks !== "" ? { marks: Number(editData.marks) } : {}),
        ...(editData.negativeMarks !== "" ? { negativeMarks: Number(editData.negativeMarks) } : {}),
        updatedAt: serverTimestamp(),
      });
      showToast("Updated", "Question meta saved.");
      setEditQ(null);
    } catch (e) {
      showToast("Update failed", e?.message || "Could not update.");
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async (q) => {
    if (!canCrud(q)) return;
    try {
      setDeleting(true);
      await deleteDoc(doc(db, QB_COLLECTION, q.id));
      showToast("Deleted", "Question removed from bank.");
    } catch (e) {
      showToast("Delete failed", e?.message || "Could not delete.");
    } finally {
      setDeleting(false);
    }
  };

  /* ---------- UI ---------- */
  if (schoolLoading) return <div className="p-5 text-center"><Spinner /></div>;

  return (
    <div className="container-fluid p-0">
      {/* Toasts */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg="light"
          onClose={() => setToast(s => ({ ...s, show: false }))}
          show={toast.show}
          autohide={false}
        >
          <Toast.Header>
            <strong className="me-auto">{toast.title || "Saved"}</strong>
            <small>just now</small>
          </Toast.Header>
          <Toast.Body>{toast.body}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Bulk Upload Modal */}
      <Modal show={bulkOpen} onHide={() => setBulkOpen(false)} size="lg" scrollable centered>
        <Modal.Header closeButton>
          <Modal.Title>Bulk Upload Questions (XLSX / CSV)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            Supported types: MCQ, Multi_Correct, True_False, Descriptive, MCQ_Descriptive,
            Assertion_Reason, Statement_Based, Match, Case_Based.<br/>
            Put one question per row. Headers are case-insensitive.
          </Alert>

          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge bg="secondary" className="me-2">Templates:</Badge>
            {Object.keys(templates).map(k => (
              <Button key={k} size="sm" variant="outline-dark" onClick={() => downloadTemplate(k)}>
                {k.replace("_"," ").toUpperCase()}
              </Button>
            ))}
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Choose .xlsx or .csv</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                try {
                  await loadBulkFile(f);
                  showToast("Loaded", "Parsed file successfully.");
                } catch (err) {
                  showToast("Parse failed", err?.message || "Could not parse file.");
                }
              }}
            />
          </Form.Group>

          {bulkParsed.length > 0 && (
            <>
              <div className="mb-2">
                <Badge bg="primary" className="me-2">Rows: {bulkParsed.length}</Badge>
                <Badge bg="success" className="me-2">
                  Valid: {bulkParsed.filter(x => x.payload && !x.error).length}
                </Badge>
                <Badge bg="danger">
                  Invalid: {bulkParsed.filter(x => x.error).length}
                </Badge>
              </div>

              {bulkParsed.some(x => x.error) && (
                <Alert variant="warning">
                  <div className="fw-semibold mb-1">Rows with errors (first 10):</div>
                  <ul className="mb-0">
                    {bulkParsed.filter(x=>x.error).slice(0,10).map(x => (
                      <li key={x.index}>Row {x.index}: {x.error}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              {bulkBusy && (
                <div className="mb-3">
                  <div className="progress" style={{ height: 12 }}>
                    <div className="progress-bar" role="progressbar" style={{ width: `${bulkProgress}%` }} />
                  </div>
                  <div className="small text-muted mt-1">Uploading… {Math.round(bulkProgress)}%</div>
                </div>
              )}

              {bulkReport.total > 0 && (
                <Alert variant="light" className="border">
                  <div className="fw-semibold mb-1">Summary</div>
                  <div>Total: {bulkReport.total}</div>
                  <div>Created: {bulkReport.created}</div>
                  <div>Linked: {bulkReport.linked}</div>
                  <div>Skipped (already linked): {bulkReport.skipped}</div>
                  <div>Errors: {bulkReport.errors}</div>
                  {bulkReport.error && <div className="text-danger mt-2">Error: {bulkReport.error}</div>}
                </Alert>
              )}

              {stage.length > 0 && (
                <div className="mt-3">
                  <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                    <Badge bg="dark">Review & Edit</Badge>
                    <Button size="sm" variant="outline-secondary"                     onClick={() => selectAll(true)}
                    >
                      Select all
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => selectAll(false)}
                    >
                      Clear all
                    </Button>
                    <Button
                      size="sm"
                      variant="success"
                      disabled={bulkBusy || !stage.some(r => r.selected && !r.error)}
                      onClick={commitBulk}
                    >
                      {bulkBusy ? "Uploading…" : "Commit selected"}
                    </Button>
                  </div>

                  <div className="table-responsive">
                    <Table bordered hover size="sm" className="align-middle">
                      <thead>
                        <tr>
                          <th style={{width: 52}}>Pick</th>
                          <th style={{width: 80}}>Row #</th>
                          <th>Type / Meta</th>
                          <th>Preview</th>
                          <th style={{width: 160}}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stage.map((r) => {
                          const p = r.payload || {};
                          const type = (p.questionType || "").replace("_"," ");
                          return (
                            <tr key={r.id} className={r.error ? "table-danger" : ""}>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  disabled={!!r.error || bulkBusy}
                                  checked={!!r.selected && !r.error}
                                  onChange={() => toggleSelect(r.id)}
                                />
                              </td>
                              <td>#{r.index}</td>
                              <td>
                                <div className="small">
                                  <div><b>Type:</b> {type || "—"}</div>
                                  <div><b>Class:</b> {p.className || "—"}</div>
                                  <div><b>Subject:</b> {p.subject || "—"}</div>
                                  <div><b>Chapter:</b> {p.chapter || "—"}</div>
                                  <div><b>Topic:</b> {p.topic || "—"}</div>
                                  {r.error && <div className="text-danger mt-1"><b>Error:</b> {r.error}</div>}
                                </div>
                              </td>
                              <td>
                                <div
                                  className="small text-truncate"
                                  style={{maxWidth: 360}}
                                  // quick glance for stem/passage
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(
                                      p.questionType === "case_based"
                                        ? (p.passageHtml || "<i>Passage…</i>")
                                        : (p.questionHtml || "<i>Question…</i>")
                                    )
                                  }}
                                />
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => duplicateRow(r.id)}
                                    disabled={bulkBusy}
                                  >
                                    Duplicate
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => removeRow(r.id)}
                                    disabled={bulkBusy}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBulkOpen(false)} disabled={bulkBusy}>
            Close
          </Button>
          <Button
            variant="success"
            onClick={commitBulk}
            disabled={bulkBusy || !stage.some(r => r.selected && !r.error)}
          >
            {bulkBusy ? "Uploading…" : "Commit selected"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <h4 className="mb-2">Create Question (Shared Bank)</h4>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={() => setShowPreview(true)}>
            Preview
          </Button>
          <Button variant="outline-secondary" onClick={analyzeDuplicate}>
            Check Duplicate
          </Button>
          <Button variant="success" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save to Bank"}
          </Button>
          <Button variant="outline-dark" onClick={() => setBulkOpen(true)}>
            Bulk Upload
          </Button>
          <Button
            variant={showMine ? "dark" : "outline-dark"}
            onClick={() => setShowMine(v => !v)}
          >
            {showMine ? "Hide My Uploads" : "View My Uploads"}
          </Button>
        </div>
      </div>

      {/* Meta form */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Class</Form.Label>
                <Form.Select value={className} onChange={e => setClassName(e.target.value)}>
                  <option value="">Select…</option>
                  {CLASS_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  {!!suggest.classes.length && <optgroup label="From Bank">
                    {suggest.classes.map(c => <option key={`s-${c}`} value={c}>{c}</option>)}
                  </optgroup>}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Subject</Form.Label>
                <Form.Select value={subject} onChange={e => setSubject(e.target.value)}>
                  <option value="">Select…</option>
                  {(SUBJECTS_MAP[className] || []).map(s => <option key={s} value={s}>{s}</option>)}
                  {!!suggest.subjects.length && <optgroup label="From Bank">
                    {suggest.subjects.map(s => <option key={`sug-${s}`} value={s}>{s}</option>)}
                  </optgroup>}
                  <option value="__other__">Other…</option>
                </Form.Select>
                {subject === "__other__" && (
                  <Form.Control
                    className="mt-2"
                    placeholder="Enter custom subject"
                    value={subjectCustom}
                    onChange={e => setSubjectCustom(e.target.value)}
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Chapter</Form.Label>
                <Form.Control value={chapter} onChange={e => setChapter(e.target.value)} placeholder="Chapter…" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Topic</Form.Label>
                <Form.Control value={topic} onChange={e => setTopic(e.target.value)} placeholder="Topic…" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Question Type</Form.Label>
                <Form.Select value={questionType} onChange={e => { setQuestionType(e.target.value); }}>
                  {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Difficulty</Form.Label>
                <Form.Select value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                  {DIFFICULTY.map(d => <option key={d} value={d}>{d}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Content by type */}
      {questionType !== "case_based" && (
        <Card className="mb-3">
          <Card.Header>Question</Card.Header>
          <Card.Body>
            <JoditField
              seedKey={`q-${editorSeed}`}
              initialValue={questionHtml}
              config={fullCfg}
              onCommit={(html) => setQuestionHtml(html)}
            />
          </Card.Body>
        </Card>
      )}

      {["mcq","multi_correct","mcq_descriptive"].includes(questionType) && (
        <Card className="mb-3">
          <Card.Header>Options</Card.Header>
          <Card.Body>
            <Row className="g-3">
              {options.map((o, idx) => (
                <Col md={6} key={o.key}>
                  <div className="d-flex align-items-start gap-2">
                    <div className="pt-2 fw-semibold">{o.key}.</div>
                    <div className="flex-grow-1">
                      <JoditField
                        seedKey={`opt-${o.key}-${editorSeed}`}
                        initialValue={o.value}
                        config={miniCfg}
                        onCommit={(html) =>
                          setOptions(prev => prev.map((x,i) => i===idx ? ({ ...x, value: html }) : x))
                        }
                      />
                    </div>
                    <div className="pt-1">
                      {questionType === "multi_correct" ? (
                        <Form.Check
                          type="checkbox"
                          checked={!!o.isCorrect}
                          onChange={() => toggleCorrectMulti(idx)}
                          label="Correct"
                        />
                      ) : (
                        <Form.Check
                          type="radio"
                          name="mcqCorrect"
                          checked={!!o.isCorrect}
                          onChange={() => setCorrectSingle(idx)}
                          label="Correct"
                        />
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            {questionType === "mcq_descriptive" && (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Descriptive Part</div>
                <JoditField
                  seedKey={`desc-${editorSeed}`}
                  initialValue={descPartHtml}
                  config={fullCfg}
                  onCommit={(html) => setDescPartHtml(html)}
                />
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {questionType === "true_false" && (
        <Card className="mb-3">
          <Card.Header>Answer</Card.Header>
          <Card.Body>
            <Form.Select value={trueFalseAnswer} onChange={e => setTrueFalseAnswer(e.target.value)}>
              <option>True</option>
              <option>False</option>
            </Form.Select>
          </Card.Body>
        </Card>
      )}

      {questionType === "statement_based" && (
        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>Statements</span>
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-success" onClick={addStatement}>Add</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setStatements([emptyStatementRow()])}>Reset</Button>
            </div>
          </Card.Header>
          <Card.Body>
            {statements.map((s, i) => (
              <div key={s.id} className="border rounded p-2 mb-2">
                <div className="d-flex gap-2 align-items-center mb-2">
                  <Form.Control
                    style={{maxWidth: 90}}
                    placeholder="Key (A/B/…) "
                    value={s.key}
                    onChange={e => setStatements(prev => prev.map(x => x.id===s.id ? ({ ...x, key: e.target.value.toUpperCase().slice(0,2) }) : x))}
                  />
                  <Form.Check
                    type="switch"
                    id={`st-${s.id}`}
                    label={s.isTrue ? "True" : "False"}
                    checked={!!s.isTrue}
                    onChange={e => setStatements(prev => prev.map(x => x.id===s.id ? ({ ...x, isTrue: e.target.checked }) : x))}
                  />
                  <Button size="sm" variant="outline-danger" onClick={() => removeStatement(s.id)} disabled={statements.length<=1}>Remove</Button>
                </div>
                <JoditField
                  seedKey={`stmt-${s.id}-${editorSeed}`}
                  initialValue={s.html}
                  config={fullCfg}
                  onCommit={(html) => setStatements(prev => prev.map(x => x.id===s.id ? ({ ...x, html }) : x))}
                />
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {questionType === "assertion_reason" && (
        <Card className="mb-3">
          <Card.Header>Assertion & Reason</Card.Header>
          <Card.Body>
            <div className="mb-3">
              <div className="fw-semibold mb-1">Assertion</div>
              <JoditField
                seedKey={`asr-assert-${editorSeed}`}
                initialValue={assertionHtml}
                config={fullCfg}
                onCommit={setAssertionHtml}
              />
            </div>
            <div className="mb-3">
              <div className="fw-semibold mb-1">Reason</div>
              <JoditField
                seedKey={`asr-reason-${editorSeed}`}
                initialValue={reasonHtml}
                config={fullCfg}
                onCommit={setReasonHtml}
              />
            </div>
            <Form.Group>
              <Form.Label>Correct Option</Form.Label>
              <Form.Select value={arAnswer} onChange={e => setArAnswer(e.target.value)}>
                <option value="A">A: Both true, R explains A</option>
                <option value="B">B: Both true, R does NOT explain A</option>
                <option value="C">C: A true, R false</option>
                <option value="D">D: A false, R true</option>
              </Form.Select>
            </Form.Group>
          </Card.Body>
        </Card>
      )}

      {questionType === "match" && (
        <Card className="mb-3">
          <Card.Header>Match the Following</Card.Header>
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Number of pairs</Form.Label>
                  <Form.Control
                    type="number"
                    min={2}
                    max={20}
                    value={matchCount}
                    onChange={(e) => setMatchCount(Math.min(20, Math.max(2, Number(e.target.value) || 2)))}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Button className="mt-3" variant="outline-secondary" onClick={() => buildMatchOptions(matchCount)}>
                  Regenerate Options
                </Button>
              </Col>
            </Row>

            <div className="mt-3">
              {[0,1,2,3].map((i) => (
                <div key={i} className="d-flex align-items-center gap-3 mb-2">
                  <Form.Check
                    type="radio"
                    name="matchCorrect"
                    checked={correctMappingIdx === i}
                    onChange={() => setCorrectMappingIdx(i)}
                    label={`Option ${i+1}`}
                  />
                  <span className="text-muted">{formatOptionText(matchOptions[i])}</span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {questionType === "case_based" && (
        <>
          <Card className="mb-3">
            <Card.Header>Passage / Case</Card.Header>
            <Card.Body>
              <JoditField
                seedKey={`passage-${editorSeed}`}
                initialValue={passageHtml}
                config={fullCfg}
                onCommit={setPassageHtml}
              />
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Sub-questions</span>
              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-success" onClick={addCaseSubQ}>Add Sub-question</Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setCaseSubQs([emptyCaseSubQ()])}>Reset</Button>
              </div>
            </Card.Header>
            <Card.Body>
              {caseSubQs.map((sq, idx) => (
                <div key={sq.id} className="border rounded p-2 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="fw-semibold">Q{idx+1}</div>
                    <Button size="sm" variant="outline-danger" onClick={() => removeCaseSubQ(sq.id)} disabled={caseSubQs.length<=1}>Remove</Button>
                  </div>
                  <JoditField
                    seedKey={`caseq-${sq.id}-${editorSeed}`}
                    initialValue={sq.questionHtml}
                    config={fullCfg}
                    onCommit={(html) => setCaseSubQs(prev => prev.map(x => x.id===sq.id ? ({ ...x, questionHtml: html }) : x))}
                  />
                  <Row className="g-3 mt-2">
                    {sq.options.map((o, i) => (
                      <Col md={6} key={o.key}>
                        <div className="d-flex align-items-start gap-2">
                          <div className="pt-2 fw-semibold">{o.key}.</div>
                          <div className="flex-grow-1">
                            <JoditField
                              seedKey={`caseq-${sq.id}-${o.key}-${editorSeed}`}
                              initialValue={o.value}
                              config={miniCfg}
                              onCommit={(html) =>
                                setCaseSubQs(prev => prev.map(x => x.id===sq.id
                                  ? ({ ...x, options: x.options.map((oo,ii) => ii===i ? ({ ...oo, value: html }) : oo) })
                                  : x))
                              }
                            />
                          </div>
                          <div className="pt-1">
                            <Form.Check
                              type="radio"
                              name={`case-correct-${sq.id}`}
                              checked={sq.correctKey === o.key}
                              onChange={() =>
                                setCaseSubQs(prev => prev.map(x => x.id===sq.id ? ({ ...x, correctKey: o.key, options: x.options.map(oo => ({ ...oo, isCorrect: oo.key === o.key })) }) : x))
                              }
                              label="Correct"
                            />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}
            </Card.Body>
          </Card>
        </>
      )}

      {/* Explanation (optional) */}
      <Card className="mb-5">
        <Card.Header>Explanation (optional)</Card.Header>
        <Card.Body>
          <JoditField
            seedKey={`exp-${editorSeed}`}
            initialValue={explanationHtml}
            config={fullCfg}
            onCommit={setExplanationHtml}
          />
        </Card.Body>
      </Card>

      {/* Preview modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" scrollable centered>
        <Modal.Header closeButton><Modal.Title>Preview</Modal.Title></Modal.Header>
        <Modal.Body><PreviewBlock /></Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* My Uploads */}
      {showMine && (
        <Card className="mb-5">
          <Card.Header>My Uploads</Card.Header>
          <Card.Body>
            
            {myLoading ? (
              <div className="text-center py-3"><Spinner /></div>
            ) : myBank.length === 0 ? (
              <div className="text-muted">No questions yet.</div>
            ) : (
              <><div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
  <Button
    size="sm"
    variant="outline-secondary"
    onClick={toggleSelectAll}
    disabled={!selectableIds.length}
  >
    {allSelected ? "Clear all" : "Select all"}
  </Button>

  <Button
    size="sm"
    variant="danger"
    onClick={bulkDelete}
    disabled={!selectedIds.size || bulkDeleting}
  >
    {bulkDeleting ? "Deleting…" : `Delete selected (${selectedIds.size || 0})`}
  </Button>
</div>
              <div className="table-responsive">
                <Table bordered hover size="sm" className="align-middle">
                 <thead>
  <tr>
    <th style={{ width: 52 }}>
      <Form.Check
        type="checkbox"
        checked={allSelected}
        onChange={toggleSelectAll}
        disabled={!selectableIds.length}
        title={allSelected ? "Clear all" : "Select all"}
      />
    </th>
    <th>Updated</th>
    <th>Type</th>
    <th>Class/Subject</th>
    <th>Chapter / Topic</th>
    <th>Preview</th>
    <th style={{width: 220}}>Actions</th>
  </tr>
</thead>
                <tbody>
  {myBank.map(q => {
    const selectable = canCrud(q);
    const checked = selectedIds.has(q.id);
    return (
      <tr key={q.id}>
        <td>
          <Form.Check
            type="checkbox"
            disabled={!selectable}
            checked={checked}
            onChange={() => toggleRow(q.id)}
            title={selectable ? "Select" : "Not allowed"}
          />
        </td>

        <td>{q.updatedAt?.toDate ? q.updatedAt.toDate().toLocaleString() : "—"}</td>
        <td>{(q.questionType || "").replace("_"," ")}</td>
        <td>{q.className} / {q.subject}</td>
        <td>{q.chapter} / {q.topic}</td>
        <td>
          <div
            className="small text-truncate"
            style={{maxWidth: 320}}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                q.questionType === "case_based"
                  ? (q.passageHtml || "<i>Passage…</i>")
                  : (q.questionHtml || "<i>Question…</i>")
              )
            }}
          />
        </td>
        <td>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-primary" onClick={() => openView(q)}>View</Button>
            {selectable && (
              <>
                <Button size="sm" variant="outline-secondary" onClick={() => openEdit(q)}>Edit</Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => confirmDelete(q)}
                  disabled={deleting}
                >
                  {deleting ? "Deleting…" : "Delete"}
                </Button>
              </>
            )}
          </div>
        </td>
      </tr>
    );
  })}
</tbody>

                </Table>
              </div>
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {/* View modal */}
      <Modal show={!!viewQ} onHide={() => setViewQ(null)} size="lg" scrollable centered>
        <Modal.Header closeButton><Modal.Title>Question</Modal.Title></Modal.Header>
        <Modal.Body>
          {viewQ && (
            <>
              <div className="mb-2">
                <Badge bg="primary" className="me-2">{viewQ.className}</Badge>
                <Badge bg="secondary" className="me-2">{viewQ.subject}</Badge>
                <Badge bg="info" text="dark" className="me-2">{viewQ.difficulty}</Badge>
                <Badge bg="light" text="dark" className="me-2">{viewQ.chapter}</Badge>
                <Badge bg="light" text="dark">{viewQ.topic}</Badge>
              </div>

              {/* Stem/Passage */}
              {viewQ.questionType !== "case_based" ? (
                <div className="border rounded p-2 mb-3"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewQ.questionHtml) }} />
              ) : (
                <>
                  <div className="border rounded p-2 mb-3"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewQ.passageHtml) }} />
                  {(viewQ.caseSubQs || []).map((sq, i) => (
                    <Card key={i} className="mb-3">
                      <Card.Body>
                        <div className="fw-semibold mb-1">Q{i+1}</div>
                        <div className="border rounded p-2 mb-2"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(sq.questionHtml) }} />
                        {(sq.options || []).map((o) => (
                          <div key={o.key} className="d-flex align-items-start gap-2 mb-1">
                            <div className="fw-semibold">{o.key}.</div>
                            <div className="flex-grow-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(o.value) }} />
                            {sq.correctKey === o.key && <Badge bg="success">Correct</Badge>}
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  ))}
                </>
              )}

              {/* Options (for non-case types) */}
              {["mcq","multi_correct","mcq_descriptive"].includes(viewQ.questionType) && (
                <div className="mb-3">
                  {(viewQ.options || []).map((o) => (
                    <div key={o.key} className="d-flex align-items-start gap-2 mb-1">
                      <div className="fw-semibold">{o.key}.</div>
                      <div className="flex-grow-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(o.value) }} />
                      {o.isCorrect && <Badge bg="success">Correct</Badge>}
                    </div>
                  ))}
                  {viewQ.questionType === "mcq_descriptive" && (
                    <div className="mt-2 border rounded p-2"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewQ.descPartHtml) }} />
                  )}
                </div>
              )}

              {viewQ.questionType === "true_false" && (
                <div className="mb-3">Answer: <Badge bg="dark">{viewQ.trueFalseAnswer}</Badge></div>
              )}

              {viewQ.questionType === "statement_based" && (
                <div className="mb-3">
                  {(viewQ.statements || []).map((s, i) => (
                    <div key={i} className="d-flex gap-2 align-items-start mb-2">
                      <div className="fw-semibold">{(s.key || String.fromCharCode(65+i))}.</div>
                      <div className="flex-grow-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.html) }} />
                      <Badge bg={s.isTrue ? "success" : "danger"}>{s.isTrue ? "True" : "False"}</Badge>
                    </div>
                  ))}
                </div>
              )}

              {viewQ.questionType === "match" && (
                <>
                  <div className="mb-1 text-muted">Pairs: {viewQ.matchPairCount}</div>
                  <div className="fw-semibold mb-2">Answer Options</div>
                  {[0,1,2,3].map((idx) => (
                    <div key={idx} className="d-flex align-items-center gap-2 mb-1">
                      <div><Badge bg={viewQ.correctOptionIndex===idx ? "success" : "secondary"}>Option {idx+1}</Badge></div>
                      <span className="text-muted small">{formatOptionText((viewQ.matchOptions || [])[idx] || [])}</span>
                    </div>
                  ))}
                </>
              )}

              {viewQ.explanationHtml && (
                <div className="mt-3">
                  <div className="fw-semibold mb-1">Explanation</div>
                  <div className="border rounded p-2"
                       dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewQ.explanationHtml) }} />
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewQ(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit modal (meta only to keep dedupe consistent) */}
      <Modal show={!!editQ} onHide={() => setEditQ(null)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Meta</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Subject</Form.Label>
            <Form.Control value={editData.subject} onChange={e => setEditData(d => ({ ...d, subject: e.target.value }))} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Chapter</Form.Label>
            <Form.Control value={editData.chapter} onChange={e => setEditData(d => ({ ...d, chapter: e.target.value }))} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Topic</Form.Label>
            <Form.Control value={editData.topic} onChange={e => setEditData(d => ({ ...d, topic: e.target.value }))} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Difficulty</Form.Label>
            <Form.Select value={editData.difficulty} onChange={e => setEditData(d => ({ ...d, difficulty: e.target.value }))}>
              {DIFFICULTY.map(d => <option key={d} value={d}>{d}</option>)}
            </Form.Select>
          </Form.Group>
          <Row className="g-2">
            <Col>
              <Form.Group>
                <Form.Label>Marks</Form.Label>
                <Form.Control
                  type="number"
                  value={editData.marks}
                  onChange={e => setEditData(d => ({ ...d, marks: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Negative Marks</Form.Label>
                <Form.Control
                  type="number"
                  value={editData.negativeMarks}
                  onChange={e => setEditData(d => ({ ...d, negativeMarks: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditQ(null)}>Cancel</Button>
          <Button variant="primary" onClick={saveEdit} disabled={savingEdit}>
            {savingEdit ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
